import { Controller } from "@hotwired/stimulus"

// Controller for managing Schema.org JSON-LD generation and validation in the admin Pages form
export default class extends Controller {
  static targets = ["generateButton", "buttonText", "spinner", "textarea", "error"]

  connect() {
    console.log("Schema Generator controller connected")
    // Validate any existing content on connect
    if (this.hasTextareaTarget && this.textareaTarget.value.trim()) {
      this.validate()
    }
  }

  // Generate schema via AJAX request to backend
  async generate(event) {
    event.preventDefault()

    const url = this.generateButtonTarget.dataset.url

    // Check if page is saved
    if (!url) {
      this.showError("Cannot generate schema for unsaved page. Please save the page first.")
      return
    }

    this.setLoading(true)
    this.hideError()

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.#csrfToken()
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.error || "Failed to generate schema")
      }

      const data = await response.json()

      // Pretty format the JSON and populate textarea
      this.textareaTarget.value = JSON.stringify(data.schema, null, 2)

      // Trigger input event for any other listeners
      this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))

      // Validate the generated JSON
      this.validate()
    } catch (error) {
      console.error("Error generating schema:", error)
      this.showError(error.message || "An error occurred while generating the schema")
    } finally {
      this.setLoading(false)
    }
  }

  // Validate JSON syntax when textarea loses focus
  validate() {
    const value = this.textareaTarget.value.trim()

    // Empty is valid (will use automatic generation)
    if (!value) {
      this.hideError()
      return true
    }

    try {
      // Try to parse the JSON
      JSON.parse(value)
      this.hideError()
      return true
    } catch (error) {
      // Show validation error
      this.showError(`Invalid JSON: ${error.message}`)
      return false
    }
  }

  // Set loading state for the generate button
  setLoading(loading) {
    if (!this.hasGenerateButtonTarget) return

    this.generateButtonTarget.disabled = loading

    if (loading) {
      this.buttonTextTarget.textContent = "Generating..."
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.remove("hidden")
      }
    } else {
      this.buttonTextTarget.textContent = "Generate Schema JSON"
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.add("hidden")
      }
    }
  }

  // Display error message
  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  // Hide error message
  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.classList.add("hidden")
    this.errorTarget.textContent = ""
  }

  // Get CSRF token from meta tag
  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}